import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { api, errMsg, rp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { VoucherQr } from "@/components/VoucherQr";
import { LAYOUTS, fmtDur } from "@/pages/admin/PrintVouchers";

/** Cetak voucher untuk akun Mitra/User — template sama seperti admin. */
export default function PortalPrint() {
  const [params] = useSearchParams();
  const batch = params.get("batch") || "";
  const only = (params.get("codes") || "").split(",").filter(Boolean);
  const [vouchers, setVouchers] = useState([]);
  const [info, setInfo] = useState({});
  const [perPage, setPerPage] = useState(20);
  const [thermal, setThermal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [v, d] = await Promise.all([api.get("/portal/vouchers"), api.get("/portal/dashboard")]);
        const all = v.data;
        setVouchers(only.length ? all.filter((x) => only.includes(x.username)) : batch ? all.filter((x) => x.batch === batch) : all);
        setInfo(d.data);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [batch]);

  const brand = info.wifi_name || "ALPHANET";
  const loginUrl = info.hotspot_login_url || "";
  const L = LAYOUTS[perPage];
  const durOf = (v) => fmtDur(v.limit_uptime || "");
  /** Harga di kartu selalu harga asli untuk pembeli (Harga User), bukan harga mitra. */
  const priceOf = (v) => Number((info.card_prices || {})[v.profile] ?? info.card_price_default ?? v.price ?? 0);
  const pages = [];
  for (let i = 0; i < vouchers.length; i += perPage) pages.push(vouchers.slice(i, i + perPage));

  return (
    <div className="min-h-screen bg-background print:bg-white">
      <div className="mx-auto max-w-[210mm] px-4 py-6 print:hidden">
        <Button asChild variant="ghost" size="sm" className="rounded-full"><Link to="/portal"><ArrowLeft className="mr-1 h-4 w-4" /> Kembali</Link></Button>
        <h1 className="mt-2 font-display text-2xl font-semibold">Cetak Kartu Voucher</h1>
        <p className="text-sm text-muted-foreground" data-testid="pp-info">
          {batch ? `Batch ${batch}${vouchers[0]?.comment ? ` (${vouchers[0].comment})` : ""} · ` : only.length ? "Voucher terpilih · " : "Semua voucher saya · "}{vouchers.length} kartu ·{" "}
          {thermal ? "mode struk thermal 58mm" : `${pages.length} lembar A4 (${perPage} kartu/lembar)`}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!thermal && [10, 20, 40].map((n) => (
            <Button key={n} size="sm" variant={perPage === n ? "default" : "outline"} className="rounded-full" data-testid={`pp-per-page-${n}`} onClick={() => setPerPage(n)}>
              {n}/lembar
            </Button>
          ))}
          <Button size="sm" variant={thermal ? "default" : "outline"} className="rounded-full" data-testid="pp-thermal" onClick={() => setThermal(!thermal)}>
            Thermal 58mm
          </Button>
          <Button onClick={() => window.print()} data-testid="pp-print" className="rounded-full bg-gradient-to-r from-sky-400 via-violet-400 to-fuchsia-400 text-white">
            <Printer className="mr-1 h-4 w-4" /> Cetak / Simpan PDF
          </Button>
        </div>
      </div>

      {thermal ? (
        <div id="print-area" className="mx-auto w-[58mm]" data-testid="pp-thermal-area">
          {vouchers.map((v) => (
            <div key={v.username} className="receipt">
              <div className="r-brand">{brand}</div>
              <div className="r-label">KODE VOUCHER</div>
              <div className="r-code">{v.username}</div>
              <div className="r-qr"><VoucherQr v={v} loginUrl={loginUrl} size={120} /></div>
              <div className="r-note">Scan QR untuk login otomatis</div>
              {durOf(v) && <div className="r-row"><span>Durasi</span><span>{durOf(v)}</span></div>}
              <div className="r-price">{rp(priceOf(v))}</div>
              {info.cs_wa && <div className="r-note">CS/WA: {info.cs_wa}</div>}
              <div className="r-cut">- - - - - - - - - - - - - - -</div>
            </div>
          ))}
        </div>
      ) : (
        <div id="print-area" className="mx-auto max-w-[210mm]" data-testid="pp-sheet">
          {pages.map((page) => (
            <div key={`sheet-${page[0]?.username}`} className="sheet border-l border-t border-dashed border-neutral-400" style={{ gridTemplateColumns: `repeat(${L.cols}, minmax(0,1fr))`, display: "grid" }}>
              {page.map((v) => (
                <div key={v.username} className="cell border-b border-r border-dashed border-neutral-400">
                  <div className="brand">{brand}</div>
                  <div className="mid">
                    <div className="code">{v.username}</div>
                    <div className="qr"><VoucherQr v={v} loginUrl={loginUrl} size={L.qr} /></div>
                  </div>
                  <div className="foot">
                    <span className="dur">{durOf(v)}</span>
                    <span className="price">{rp(priceOf(v))}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {!vouchers.length && <p className="p-8 text-center text-sm text-muted-foreground print:hidden">Belum ada voucher untuk dicetak.</p>}

      <style>{`
        .sheet { width: 200mm; }
        .cell { height: ${L.h}; background: #fff; color: #111; display: flex; flex-direction: column; align-items: center; justify-content: space-between; padding: 2mm 1.5mm; }
        .cell .brand { font-size: ${L.brand}; letter-spacing: .12em; text-transform: uppercase; color: #555; font-weight: 600; }
        .cell .mid { display: flex; align-items: center; justify-content: space-between; gap: 1.5mm; width: 100%; overflow: hidden; }
        .cell .qr { background: #fff; line-height: 0; flex: none; }
        .cell .code { font-family: "JetBrains Mono", monospace; font-size: ${L.code}; font-weight: 800; letter-spacing: .06em; line-height: 1; flex: 1 1 auto; min-width: 0; text-align: center; }
        .cell .dur { font-size: ${L.foot}; color: #111; font-weight: 600; }
        .cell .price { font-size: ${L.price}; font-weight: 900; color: #000; line-height: 1; letter-spacing: -.01em; }
        .cell .foot { width: 100%; display: flex; align-items: flex-end; justify-content: space-between; font-size: ${L.foot}; color: #444; }
        .receipt { width: 58mm; background: #fff; color: #000; padding: 3mm 3mm 1mm; text-align: center; }
        .receipt .r-brand { font-size: 11pt; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
        .receipt .r-label { margin-top: 2mm; font-size: 7pt; letter-spacing: .2em; color: #333; }
        .receipt .r-code { font-family: "JetBrains Mono", monospace; font-size: 24pt; font-weight: 800; letter-spacing: .12em; line-height: 1.1; }
        .receipt .r-qr { display: flex; justify-content: center; margin-top: 2mm; line-height: 0; }
        .receipt .r-row { display: flex; justify-content: space-between; font-size: 8pt; margin-top: 1mm; }
        .receipt .r-price { margin-top: 1.5mm; font-size: 20pt; font-weight: 900; letter-spacing: -.01em; }
        .receipt .r-note { margin-top: 1.5mm; font-size: 7pt; color: #222; }
        .receipt .r-cut { margin-top: 2mm; font-size: 7pt; color: #888; }
        @media print {
          @page { size: ${thermal ? "58mm auto" : "A4"}; margin: ${thermal ? "0" : "5mm"}; }
          body { background: #fff; }
          .sheet { page-break-after: always; }
          .receipt { page-break-after: always; }
        }
      `}</style>
    </div>
  );
}
